import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import {
  Mail,
  Target,
  PartyPopper,
  XCircle,
  Package,
  CheckSquare,
  AlertOctagon,
  Coins,
  AlertTriangle,
  TrendingDown,
  ShieldCheck,
  Eye,
  FileText,
  CreditCard,
  Bell
} from 'lucide-react';
import { NotificationSkeleton, EmptyState } from '../components/skeletons';

const C = {
  green: '#1B5C3A', greenDark: '#0F3D26', greenLight: '#E8F5EE',
  gold: '#F5A623', goldLight: '#FFF8E7', goldDark: '#C4831A',
  cream: '#FAF8F3', charcoal: '#1A1A1A', muted: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF',
  red: '#EF4444', redLight: '#FEF2F2',
  blue: '#3B82F6', blueLight: '#EFF6FF',
};

const F = { display: "'Fraunces', Georgia, serif", body: "'Plus Jakarta Sans', sans-serif" };

export default function Notifications() {
  const { fetchUnread } = useNotifications();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const fetchNotifications = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      const res = await notificationAPI.list({ page: p, limit: 20 });
      if (res.data.success) {
        if (p === 1) setNotifications(res.data.data);
        else setNotifications(prev => [...prev, ...res.data.data]);
        setTotal(res.data.pagination.total);
        setPage(p);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      fetchUnread(); // Refresh sidebar count immediately
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleClick = (notif) => {
    if (!notif.isRead) handleMarkAsRead(notif._id);

    // Navigate based on type
    if (notif.type === 'offer_received') {
      navigate(`/buyer/demands/${notif.data.demandId?._id || notif.data.demandId}`);
    } else if (notif.type === 'lead_received') {
      navigate(`/vendor/demands`);
    } else if (notif.type === 'offer_accepted' || notif.type === 'offer_rejected') {
      navigate(`/vendor/offers`);
    } else if (notif.type === 'bulk_bid') {
      navigate(`/vendor/bulk`);
    } else if (notif.type === 'bulk_accepted' || notif.type === 'bulk_rejected') {
      navigate(`/buyer/bulk`);
    } else if (notif.type === 'wallet_recharge' || notif.type === 'wallet_low' || notif.type === 'lead_skipped' || notif.type === 'wallet_credit_added') {
      navigate(`/vendor/wallet`);
    } else if (notif.type === 'kyc_status') {
      navigate(`/vendor/kyc`);
    } else if (notif.type === 'admin_kyc_review') {
      navigate(`/admin/kyc`);
    } else if (notif.type === 'admin_demand_review') {
      navigate(`/admin/demands`);
    } else if (notif.type === 'admin_platform_fee') {
      navigate(`/admin/vendors`); // Or a specific payments page if exists
    } else if (notif.type === 'admin_credit_request') {
      navigate(`/admin/credit-requests`);
    }
  };

  const getIcon = (type) => {
    const size = 20;
    switch (type) {
      case 'offer_received': return <Mail size={size} color={C.blue} />;
      case 'lead_received': return <Target size={size} color={C.green} />;
      case 'offer_accepted': return <PartyPopper size={size} color={C.green} />;
      case 'offer_rejected': return <XCircle size={size} color={C.red} />;
      case 'bulk_bid': return <Package size={size} color={C.blue} />;
      case 'bulk_accepted': return <CheckSquare size={size} color={C.green} />;
      case 'bulk_rejected': return <AlertOctagon size={size} color={C.red} />;
      case 'wallet_recharge': return <Coins size={size} color={C.green} />;
      case 'wallet_low': return <AlertTriangle size={size} color={C.gold} />;
      case 'lead_skipped': return <TrendingDown size={size} color={C.red} />;
      case 'kyc_status': return <ShieldCheck size={size} color={C.green} />;
      case 'admin_kyc_review': return <Eye size={size} color={C.blue} />;
      case 'admin_demand_review': return <FileText size={size} color={C.blue} />;
      case 'admin_platform_fee': return <CreditCard size={size} color={C.gold} />;
      case 'admin_credit_request': return <CreditCard size={size} color={C.blue} />;
      case 'wallet_credit_added': return <Coins size={size} color={C.green} />;
      default: return <Bell size={size} color={C.muted} />;
    }
  };

  return (
    <div style={{ paddingBottom: 60 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: F.display, fontSize: 24, fontWeight: 900, color: C.charcoal }}>Notifications</h1>
        {notifications.some(n => !n.isRead) && (
          <button
            onClick={async () => {
              await notificationAPI.markAllAsRead();
              setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
              fetchUnread(); // Refresh sidebar count immediately
            }}
            style={{ background: 'none', border: 'none', color: C.green, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {loading && page === 1 ? (
        <NotificationSkeleton count={5} />
      ) : notifications.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="No notifications yet"
          description="We will notify you here when you receive new offers, demand updates, or messages."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {notifications.map(n => (
            <div
              key={n._id}
              onClick={() => handleClick(n)}
              style={{
                background: n.isRead ? C.white : C.greenLight,
                padding: 16,
                borderRadius: 12,
                border: `1.5px solid ${n.isRead ? C.border : C.green}`,
                cursor: 'pointer',
                display: 'flex',
                gap: 16,
                transition: 'all 0.2s',
                boxShadow: n.isRead ? 'none' : '0 4px 12px rgba(27,92,58,0.1)'
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12, background: n.isRead ? C.cream : C.white,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0
              }}>
                {getIcon(n.type)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <h4 style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal }}>{n.title}</h4>
                  <span style={{ fontSize: 11, color: C.muted }}>
                    {new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <p style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{n.message}</p>
              </div>
              {!n.isRead && (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, alignSelf: 'center' }} />
              )}
            </div>
          ))}

          {notifications.length < total && (
            <button
              onClick={() => fetchNotifications(page + 1)}
              disabled={loading}
              style={{
                padding: '12px', background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12,
                color: C.green, fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 12
              }}
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
